/**
 * ModuleDocsModal — R11 #287, R12 #314, #643(변경 이력), #688(스펙 문서 뷰 재구성).
 *
 * queue #688 §UAT-2026-07-14-R688 §R688-5 — 실 스펙 문서(docs/common-specs) 를 본문 우선으로
 * 렌더한다: 좌 목차(StandardsToc) + 우 본문(MarkdownView) — 둘 다 dev-standards-viewer 재사용
 * (#647 cross-feature re-export 선례 · 신규 마크다운 렌더러 작성 금지). 기존 메타데이터 4종
 * (모듈 메타데이터 dl · 공개 API · 요약 발췌(구 common-capabilities 발췌) · 변경 이력)은
 * `<details>` 접힘 섹션으로 강등 — 콘텐츠/i18n 키는 불변, 위치만 이동.
 *
 * lazy fetch — 모달이 열릴 때만 `useModuleSpec(code, { enabled: open })` 를 활성화한다(트리
 * 클릭만으로 143 노드 전체 요청이 나가는 사고 방지 · #655 OOM 계보 재발 방지).
 *
 * SUPERSEDED 노드(C032/C033/C034/C038)는 API 호출 없이 `node.supersededBy` 기반 안내만
 * 렌더한다(BE 도 동일 코드를 404-003 로 차단 — 헛된 왕복 0).
 *
 * queue #693 §UAT-2026-07-14-R693 §R693-3/§R693-5 — 헤더 액션 바에 [.md 다운로드]/[전문 복사]
 * (이미 fetch 한 `specQuery.data.markdown` 재사용 · 추가 요청 0 · 404/502/SUPERSEDED 시 미노출)
 * + Ctrl+K 통합검색 'spec' 그룹에서 진입 시 마운트 즉시 오픈 + 모달 스코프 앵커 스크롤(전역
 * `document.querySelector` 금지 — id 충돌·목차 링크 혼선 방지) 을 추가한다.
 */
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState, ErrorState, CopyButton } from '@ui-kit';
import { MarkdownView, StandardsToc } from '../../dev-standards-viewer';
import { downloadBlob } from '../../_shared/lib/downloadBlob';
import { highlightAnchorTarget } from '../../_shared/lib/highlightAnchorTarget';
import type { ModuleNode, ModuleMaturity, ApiError } from '../types/showcase';
import { useModuleSpec } from '../hooks/useModuleSpec';
import { SUPERSEDE_JUMP_DISABLED_CODES } from '../data/maturity';
import anchorsJson from '../data/common-capabilities-anchors.json';
import moduleMaturityJson from '../data/module-maturity.json';

/** 본문 스크롤/하이라이트 대상을 스코프하는 컨테이너 id — `MarkdownView contentId` 와 정합 */
const SPEC_CONTENT_ID = 'module-docs-spec-content';

const anchors = anchorsJson as Record<string, string>;
// queue #643 §R643-7: 신규 모달 대신 이 모달 본문에 "변경 이력" 섹션을 추가한다.
const MODULE_MATURITY = moduleMaturityJson as unknown as ModuleMaturity;

interface Props {
  node: ModuleNode;
  /** #617/#688 — SUPERSEDED 노드의 "대체 능력 스펙 보기" 클릭 시 트리 노드 전환(jumpToCode 재사용). */
  onJumpToCode?: (code: string) => void;
  /**
   * #693 §R693-5 — Ctrl+K 통합검색 'spec' 그룹에서 `/showcase?spec=<slug>#<CODE>` 로 진입 시
   * 마운트 즉시 모달을 연다(사용자 클릭 없이) + 본문 로드 후 이 슬러그로 스크롤+하이라이트.
   * 슬러그가 문서에 없으면(스펙 개정으로 섹션 삭제) 스크롤만 생략 — 오류 화면 금지.
   */
  autoOpenSpecSlug?: string | null;
  /** #693 §R693-5 — autoOpenSpecSlug 로 열린 모달이 닫힐 때 상위가 `?spec=` 을 정리하도록 위임 */
  onAutoOpenConsumed?: () => void;
}

const codeStyle: React.CSSProperties = {
  fontFamily: 'var(--font-family-mono)',
  fontSize: 'var(--font-size-xs)',
  background: 'var(--color-surface-elevated)',
  border: '1px solid var(--color-surface-border)',
  padding: '2px var(--spacing-2)',
  borderRadius: 'var(--radius-sm)',
  color: 'var(--color-brand-primary)',
};

const excerptBlockStyle: React.CSSProperties = {
  fontFamily: 'var(--font-family-mono)',
  fontSize: 'var(--font-size-xs)',
  background: 'var(--color-surface-muted)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-sm)',
  padding: 'var(--spacing-3)',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  color: 'var(--color-text-secondary)',
  lineHeight: 1.6,
  maxHeight: '240px',
  overflowY: 'auto',
};

const detailsSummaryStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
  color: 'var(--color-text-secondary)',
  cursor: 'pointer',
  padding: 'var(--spacing-2) 0',
};

const missingBoxStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-muted)',
  fontStyle: 'italic',
  padding: 'var(--spacing-2)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--color-surface-muted)',
};

/** Derive anchor key from docsAnchor path like "common-capabilities.md#c001" → "c001" */
function extractAnchorKey(docsAnchor: string | null | undefined): string | null {
  if (!docsAnchor) return null;
  const hash = docsAnchor.includes('#') ? docsAnchor.split('#')[1] : docsAnchor;
  return hash ? hash.toLowerCase() : null;
}

/** §R688-4 매핑 규약(`docs/common-specs/<artifact>/`) — 정확한 파일명은 BE 만 해석하므로
 * 404/502 강등 화면에는 디렉터리까지만 안내한다. */
function specDirPath(artifact: string): string {
  return `docs/common-specs/${artifact}`;
}

/** 본문 스켈레톤 — 목차 자리 포함(§R688-5 5상태 loading) */
function SpecBodySkeleton() {
  return (
    <div style={{ display: 'flex', gap: 'var(--spacing-6)' }} aria-hidden="true">
      <div style={{ minWidth: 180, display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            style={{
              height: 14,
              borderRadius: 'var(--radius-sm)',
              background: 'var(--color-surface-muted)',
              animation: 'pulse 1.5s ease-in-out infinite',
              opacity: 1 - i * 0.08,
            }}
          />
        ))}
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)' }}>
        <div style={{ height: 28, width: '60%', borderRadius: 'var(--radius-sm)', background: 'var(--color-surface-muted)', animation: 'pulse 1.5s ease-in-out infinite' }} />
        <div style={{ height: 100, borderRadius: 'var(--radius-md)', background: 'var(--color-surface-muted)', animation: 'pulse 1.5s ease-in-out infinite' }} />
        <div style={{ height: 60, borderRadius: 'var(--radius-md)', background: 'var(--color-surface-muted)', animation: 'pulse 1.5s ease-in-out infinite' }} />
      </div>
    </div>
  );
}

export function ModuleDocsModal({ node, onJumpToCode, autoOpenSpecSlug, onAutoOpenConsumed }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [excerptOpen, setExcerptOpen] = useState(false);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  // #693 §R693-5 — autoOpenSpecSlug 로 열렸는지 추적(닫힐 때만 상위에 `?spec=` 정리를 위임).
  const autoOpenedRef = useRef(false);
  const highlightCleanupRef = useRef<(() => void) | null>(null);

  // #617/§R688-6 — SUPERSEDED(supersededBy != null) 는 API 호출 없이 안내만 렌더.
  // `!== null` 강비교 금지(JSON 상 optional 필드가 undefined 로 올 수 있음).
  const successor = node.supersededBy;
  const isSuperseded = successor != null;

  const specQuery = useModuleSpec(node.code, { enabled: open && !isSuperseded });
  const specError = specQuery.error as ApiError | null;
  const isNotFound = specQuery.isError && specError?.statusCode === 404;
  const isSourceUnavailable = specQuery.isError && specError?.statusCode === 502;
  const isOtherError = specQuery.isError && !isNotFound && !isSourceUnavailable;
  // §R693-3 — 반출 액션(다운로드/복사)은 본문 로드 성공 시에만 노출(빈 파일 반출 차단).
  const canExportSpec = Boolean(open && !isSuperseded && specQuery.data);

  // Derive anchor from docsAnchor or fall back to node.code (e.g. "C001" → "c001")
  const anchorKey = extractAnchorKey(node.docsAnchor) ?? node.code.toLowerCase();
  const excerpt = anchors[anchorKey] ?? null;

  // queue #643 §R643-7: 변경 이력 — module-maturity.json[artifact].commits[] (없으면 fallback)
  const maturityEntry = MODULE_MATURITY.modules?.[node.artifact];
  const commits = maturityEntry?.gitAvailable ? maturityEntry.commits : [];
  const hasChangelog = commits.length > 0;

  // §R688-5/6 — 404/502 강등 시 '요약 발췌' 접힘을 자동으로 편다(현행 자산으로 강등).
  useEffect(() => {
    if (isNotFound || isSourceUnavailable) setExcerptOpen(true);
  }, [isNotFound, isSourceUnavailable]);

  /**
   * #693 §R693-5 — 마운트 시 `autoOpenSpecSlug` 가 있으면 사용자 클릭 없이 모달을 연다
   * (Ctrl+K 'spec' 그룹 선택 → `/showcase?spec=<slug>#<CODE>` 진입). SUPERSEDED 노드는
   * 스펙 API 호출 자체가 없으므로 자동 오픈 대상에서 제외한다. 마운트 1회만 판정 —
   * 노드가 바뀌면 상위(CommonShowcasePage)가 `key={node.code}` 로 이 컴포넌트를 재마운트한다.
   */
  useEffect(() => {
    if (autoOpenSpecSlug && !isSuperseded) {
      setOpen(true);
      autoOpenedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * #693 §R693-5 — 본문 로드 성공 후 `autoOpenSpecSlug` 대상으로 스크롤+하이라이트.
   * ★조회는 `#module-docs-spec-content` 컨테이너 스코프로 한정(전역 `document.querySelector`
   * 금지 — 카탈로그 트리/목차(StandardsToc) 링크와 id 충돌 가능 · E66/E67 셀렉터 사고 계보).
   * ★★heading 의 `id` 자체는 매칭 대상이 아니다 — `MarkdownView` 의 `rehype-sanitize` 기본
   * 스키마가 `id`/`name` 을 `clobber` 목록으로 처리해 `user-content-` 접두를 붙인다(DOM
   * clobbering 방지 · hast-util-sanitize 기본값). 반면 `rehypeAutolinkHeadings` 가 헤딩 끝에
   * 덧붙이는 `<a href="#<slug>">` 의 `href` 는 clobber 대상이 아니라 원본 슬러그를 그대로 보존
   * — 이 앵커의 `href` 로 대상을 찾고, 그 앵커가 속한 heading 엘리먼트를 하이라이트한다.
   * 슬러그가 문서에 없으면(스펙 개정으로 섹션 삭제) 스크롤만 생략 — 오류 화면 금지(§R693-5).
   */
  useEffect(() => {
    if (!open || !autoOpenSpecSlug || !specQuery.data) return undefined;
    const frame = requestAnimationFrame(() => {
      const container = document.getElementById(SPEC_CONTENT_ID);
      const anchor = container?.querySelector<HTMLElement>(`a[href="#${autoOpenSpecSlug}"]`);
      const target = anchor?.closest<HTMLElement>('h1, h2, h3, h4, h5, h6') ?? anchor ?? undefined;
      if (!target) return;
      target.scrollIntoView({ block: 'start' });
      highlightCleanupRef.current?.();
      highlightCleanupRef.current = highlightAnchorTarget(target);
    });
    return () => {
      cancelAnimationFrame(frame);
      highlightCleanupRef.current?.();
      highlightCleanupRef.current = null;
    };
  }, [open, autoOpenSpecSlug, specQuery.data]);

  /** #693 §R693-5 — 닫기 경로(Esc·backdrop·✕·하단 버튼·후계 이동) 공통: autoOpen 이었으면 상위에 `?spec=` 정리 위임 */
  function closeModal() {
    setOpen(false);
    if (autoOpenedRef.current) {
      autoOpenedRef.current = false;
      onAutoOpenConsumed?.();
    }
  }

  // Esc key to close
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeModal();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Focus close button when modal opens
  useEffect(() => {
    if (open) {
      const id = requestAnimationFrame(() => closeBtnRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
    // 모달을 닫으면 다음 오픈 시 발췌 자동펼침 상태를 초기화(이전 노드 상태 잔존 방지)
    setExcerptOpen(false);
  }, [open]);

  function handleJumpToSuccessor(code: string) {
    closeModal();
    onJumpToCode?.(code);
  }

  /** §R693-3 — filename = `data.path` 의 basename(BE 규약 `<CODE>-<kebab-name>.md` 그대로 · FE 재조합 금지) */
  function specFilename(): string {
    const path = specQuery.data?.path;
    const base = path ? path.split('/').pop() : undefined;
    return base && base.length > 0 ? base : `${node.code}.md`;
  }

  function handleDownloadMd() {
    if (!specQuery.data) return;
    const blob = new Blob([specQuery.data.markdown], { type: 'text/markdown;charset=utf-8' });
    downloadBlob(blob, specFilename());
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 'var(--spacing-1)',
          marginTop: 'var(--spacing-3)',
          color: 'var(--color-brand-primary)',
          fontSize: 'var(--font-size-sm)',
          background: 'none',
          border: '1px solid var(--color-brand-primary)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--spacing-1) var(--spacing-3)',
          cursor: 'pointer',
          fontFamily: 'var(--font-family-sans)',
        }}
        aria-haspopup="dialog"
      >
        {t('showcase.panel.docsLink')} →
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="module-docs-modal-title"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
          }}
          onClick={closeModal}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--color-surface-background)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--spacing-6)',
              minWidth: '480px',
              maxWidth: '1080px',
              width: '92vw',
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
              maxHeight: '88vh',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: 'var(--spacing-4)',
                flexShrink: 0,
              }}
            >
              <h2
                id="module-docs-modal-title"
                style={{
                  margin: 0,
                  fontSize: 'var(--font-size-lg)',
                  fontWeight: 700,
                  color: 'var(--color-text-primary)',
                }}
              >
                {t('showcase.panel.specDoc.title')} — {node.code} · {node.nameKo} ({node.nameEn})
              </h2>

              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', flexShrink: 0 }}>
                {/* #693 §R693-3 — 헤더 액션 바([.md 다운로드]·[전문 복사]). 본문 로드 성공 시에만
                    노출(404/502/SUPERSEDED 는 빈 파일 반출을 막기 위해 숨김). */}
                {canExportSpec && (
                  <>
                    <button
                      type="button"
                      onClick={handleDownloadMd}
                      data-testid="spec-download-md-btn"
                      aria-label={t('showcase.panel.specDoc.downloadMd')}
                      style={{
                        padding: 'var(--spacing-1) var(--spacing-3)',
                        border: '1px solid var(--color-surface-border)',
                        borderRadius: 'var(--radius-md)',
                        background: 'var(--color-surface-background)',
                        color: 'var(--color-text-secondary)',
                        fontSize: 'var(--font-size-xs)',
                        fontFamily: 'var(--font-family-sans)',
                        cursor: 'pointer',
                      }}
                    >
                      {t('showcase.panel.specDoc.downloadMd')}
                    </button>
                    <CopyButton
                      text={specQuery.data!.markdown}
                      ariaLabel={t('showcase.panel.specDoc.copyAllAria')}
                      copyLabel={t('showcase.panel.specDoc.copyAll')}
                      copiedLabel={t('showcase.takeThis.copied')}
                    />
                  </>
                )}
                <button
                  ref={closeBtnRef}
                  type="button"
                  onClick={closeModal}
                  aria-label={t('showcase.panel.docsModal.close')}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 20,
                    color: 'var(--color-text-muted)',
                    lineHeight: 1,
                    padding: 'var(--spacing-1)',
                    flexShrink: 0,
                  }}
                >
                  ✕
                </button>
              </div>
            </div>

            {/* §R688-5 본문 영역 — SUPERSEDED / loading / notFound / sourceUnavailable / otherError / default */}
            <div
              tabIndex={0}
              data-testid="module-docs-modal-body"
              style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}
            >
              {isSuperseded ? (
                <EmptyState
                  title={
                    successor === 'gateway-self'
                      ? t('showcase.supersede.gatewaySelfNotice')
                      : t('showcase.panel.specDoc.superseded', { code: successor })
                  }
                  action={
                    successor !== 'gateway-self' ? (
                      SUPERSEDE_JUMP_DISABLED_CODES.has(node.code) ? (
                        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-family-mono)' }}>
                          {t('showcase.supersede.successorLabelOnly', { code: successor })}
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleJumpToSuccessor(successor as string)}
                          style={{
                            background: 'none',
                            border: '1px solid var(--color-brand-primary)',
                            borderRadius: 'var(--radius-md)',
                            padding: 'var(--spacing-2) var(--spacing-4)',
                            color: 'var(--color-brand-primary)',
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 600,
                            cursor: 'pointer',
                            fontFamily: 'var(--font-family-sans)',
                          }}
                        >
                          {t('showcase.panel.specDoc.supersededLink')}
                        </button>
                      )
                    ) : undefined
                  }
                />
              ) : open && specQuery.isLoading ? (
                <SpecBodySkeleton />
              ) : isNotFound ? (
                <EmptyState
                  title={t('showcase.panel.specDoc.notFound')}
                  description={specDirPath(node.artifact)}
                  action={
                    <CopyButton
                      text={specDirPath(node.artifact)}
                      ariaLabel={t('showcase.takeThis.copyAria', { block: t('showcase.panel.specDoc.repoPath') })}
                      copyLabel={t('showcase.takeThis.copy')}
                      copiedLabel={t('showcase.takeThis.copied')}
                    />
                  }
                />
              ) : isSourceUnavailable ? (
                <div
                  role="status"
                  style={{
                    padding: 'var(--spacing-4)',
                    background: 'var(--color-warning)',
                    color: 'var(--color-text-inverse)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-sm)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--spacing-2)',
                  }}
                >
                  <span>{t('showcase.panel.specDoc.sourceUnavailable')}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)', flexWrap: 'wrap' }}>
                    <code style={{ ...codeStyle, background: 'rgba(255,255,255,0.2)', color: 'var(--color-text-inverse)' }}>
                      {specDirPath(node.artifact)}
                    </code>
                    <button
                      type="button"
                      onClick={() => void specQuery.refetch()}
                      style={{
                        background: 'var(--color-surface-background)',
                        color: 'var(--color-brand-primary)',
                        border: 'none',
                        borderRadius: 'var(--radius-sm)',
                        padding: 'var(--spacing-1) var(--spacing-3)',
                        cursor: 'pointer',
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 600,
                      }}
                    >
                      {t('showcase.panel.specDoc.retry')}
                    </button>
                  </div>
                </div>
              ) : isOtherError ? (
                <ErrorState
                  message={specError?.message}
                  onRetry={() => void specQuery.refetch()}
                />
              ) : specQuery.data ? (
                <div className="module-docs-toc-wrapper" style={{ display: 'flex', gap: 'var(--spacing-6)' }}>
                  <div style={{ display: 'flex' }} className="module-docs-toc-inner">
                    <StandardsToc markdownText={specQuery.data.markdown} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <MarkdownView markdownText={specQuery.data.markdown} contentId={SPEC_CONTENT_ID} />
                  </div>
                </div>
              ) : null}
            </div>

            {/* §R688-5 하단 접힘 메타 4종 — 콘텐츠/i18n 키 불변, <details> 로 위치만 이동 */}
            <div style={{ marginTop: 'var(--spacing-4)', borderTop: '1px solid var(--color-surface-border)', paddingTop: 'var(--spacing-2)', flexShrink: 0 }}>
              <details>
                <summary style={detailsSummaryStyle}>{t('showcase.panel.docsModal.metadata')}</summary>
                <dl
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'auto 1fr',
                    gap: 'var(--spacing-2) var(--spacing-4)',
                    fontSize: 'var(--font-size-sm)',
                    padding: 'var(--spacing-2) 0',
                  }}
                >
                  <dt style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>code</dt>
                  <dd style={{ margin: 0 }}>
                    <code style={codeStyle}>{node.code}</code>
                  </dd>

                  <dt style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>nameKo</dt>
                  <dd style={{ margin: 0, color: 'var(--color-text-primary)' }}>{node.nameKo}</dd>

                  <dt style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>nameEn</dt>
                  <dd style={{ margin: 0, color: 'var(--color-text-secondary)' }}>{node.nameEn}</dd>

                  <dt style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>artifact</dt>
                  <dd style={{ margin: 0 }}>
                    <code style={codeStyle}>{node.artifact}</code>
                  </dd>

                  <dt style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>version</dt>
                  <dd style={{ margin: 0 }}>
                    <code style={codeStyle}>{node.version || 'N/A'}</code>
                  </dd>

                  <dt style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>since</dt>
                  <dd style={{ margin: 0, color: 'var(--color-text-secondary)' }}>{node.since}</dd>

                  <dt style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>demoEndpoint</dt>
                  <dd style={{ margin: 0 }}>
                    {node.demoEndpoint ? (
                      <code style={codeStyle}>{node.demoEndpoint}</code>
                    ) : (
                      <span style={{ color: 'var(--color-text-muted)', fontStyle: 'italic' }}>{t('showcase.panel.docsModal.demoEndpointNone')}</span>
                    )}
                  </dd>
                </dl>
              </details>

              {node.publicApis.length > 0 && (
                <details>
                  <summary style={detailsSummaryStyle}>{t('showcase.panel.publicApis')}</summary>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-2)', padding: 'var(--spacing-2) 0' }}>
                    {node.publicApis.map((api) => (
                      <code key={api} style={codeStyle}>{api}</code>
                    ))}
                  </div>
                </details>
              )}

              {/* #314/#688: 구 common-capabilities 발췌 — '요약 발췌' 로 강등. 404/502 시 자동 펼침. */}
              <details open={excerptOpen} onToggle={(e) => setExcerptOpen(e.currentTarget.open)}>
                <summary style={detailsSummaryStyle}>{t('showcase.panel.docsModal.excerptFold')}</summary>
                <div style={{ padding: 'var(--spacing-2) 0' }}>
                  {excerpt ? (
                    <section aria-label={t('showcase.panel.docsModal.excerpt')}>
                      <pre style={excerptBlockStyle}>{excerpt}</pre>
                    </section>
                  ) : (
                    <p style={missingBoxStyle}>{t('showcase.panel.docsModal.excerptMissing')}</p>
                  )}
                </div>
              </details>

              {/* queue #643 §R643-7: 변경 이력 — 신규 모달 대신 이 본문에 섹션만 추가 */}
              <details>
                <summary style={detailsSummaryStyle}>{t('showcase.maturity.changelog.title')}</summary>
                <div style={{ padding: 'var(--spacing-2) 0' }}>
                  {hasChangelog ? (
                    <div>
                      {MODULE_MATURITY.shallow && (
                        <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontStyle: 'italic', marginBottom: 'var(--spacing-2)' }}>
                          {t('showcase.maturity.changelog.shallowNote')}
                        </p>
                      )}
                      <ul style={{ margin: 0, paddingLeft: 'var(--spacing-4)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                        {commits.map((c, idx) => (
                          <li key={`${c.date}-${idx}`} style={{ marginBottom: 'var(--spacing-1)' }}>
                            <code style={{ fontFamily: 'var(--font-family-mono)', color: 'var(--color-text-muted)' }}>{c.date}</code>
                            {' — '}
                            {c.subject}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <p style={missingBoxStyle}>{t('showcase.maturity.changelog.empty')}</p>
                  )}
                </div>
              </details>
            </div>

            <div
              style={{
                marginTop: 'var(--spacing-4)',
                display: 'flex',
                justifyContent: 'flex-end',
                flexShrink: 0,
              }}
            >
              <button
                type="button"
                onClick={closeModal}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-5)',
                  background: 'var(--color-brand-primary)',
                  color: 'var(--color-text-inverse)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                  fontSize: 'var(--font-size-sm)',
                  fontFamily: 'var(--font-family-sans)',
                  fontWeight: 600,
                }}
              >
                {t('showcase.panel.docsModal.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.5; } }
        @media (max-width: 768px) {
          .module-docs-toc-wrapper { flex-direction: column; }
          .module-docs-toc-inner { display: none !important; }
        }
      `}</style>
    </>
  );
}
